import { useCallback, useEffect, useRef, useState } from 'react';
import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';
import {
  apiErrorFrom,
  readableError,
} from '../../AdminDashboard/ScenarioBuilder/errors';
import type { DevPlanLaunchSelection } from './types';

type Request = {
  internalCode: string;
  blueprint_slug?: string;
  recommendation_item_id?: number;
};
type Intent = {
  sessionId: number;
  resumed: boolean;
  selection?: DevPlanLaunchSelection;
};

/** A 202 creates no session and spends no coins. Repeat only that explicit
 * preparation response, retaining the trainee's exact approved selection. */
export default function useSoloLaunch(planCode: string) {
  const [intent, setIntent] = useState<Intent>();
  const [error, setError] = useState('');
  const [preparing, setPreparing] = useState(false);
  const active = useRef<{
    controller: AbortController;
    timer?: ReturnType<typeof setTimeout>;
  }>();
  const cancel = useCallback(() => {
    active.current?.controller.abort();
    if (active.current?.timer) clearTimeout(active.current.timer);
    active.current = undefined;
  }, []);
  useEffect(() => () => cancel(), [cancel, planCode]);
  const clear = useCallback(() => {
    cancel();
    setIntent(undefined);
    setPreparing(false);
    setError('');
  }, [cancel]);

  const start = useCallback(
    (request: Request) => {
      cancel();
      setIntent(undefined);
      setError('');
      setPreparing(false);
      const run: NonNullable<typeof active.current> = {
        controller: new AbortController(),
      };
      active.current = run;
      const body = {
        blueprint_slug: request.blueprint_slug,
        recommendation_item_id: request.recommendation_item_id,
      };
      const launch = async () => {
        try {
          const response = await apiCaller({
            path: `/devplan/plans/${encodeURIComponent(planCode)}/nodes/${encodeURIComponent(request.internalCode)}/launch`,
            method: 'POST',
            data: { ...body },
            apiUrl: API_URL,
            signal: run.controller.signal,
          });
          if (active.current !== run) return;
          const result = response.result;
          if (
            response.status === 202 &&
            result?.code === 'assessment_preparing'
          ) {
            // A default pick becomes explicit after the server chooses it, so
            // another room's progress cannot change the exercise while it prepares.
            if (
              !body.blueprint_slug &&
              typeof result.blueprint_slug === 'string' &&
              result.blueprint_slug
            ) {
              body.blueprint_slug = result.blueprint_slug;
            }
            setPreparing(true);
            const seconds = Number(result.retry_after_seconds);
            const delay = Number.isFinite(seconds)
              ? Math.max(1, Math.min(30, seconds)) * 1000
              : 3000;
            run.timer = setTimeout(() => void launch(), delay);
            return;
          }
          if (response.status < 200 || response.status >= 300)
            throw apiErrorFrom(response.status, result);
          if (
            (response.status !== 200 && response.status !== 201) ||
            !Number.isInteger(result?.session_id) ||
            result.session_id <= 0
          ) {
            throw new Error(
              'The session start could not be confirmed. Refresh the plan before trying again.',
            );
          }
          setPreparing(false);
          setIntent({
            sessionId: result.session_id,
            resumed: Boolean(result.resumed),
            selection: result.selection,
          });
        } catch (failure) {
          if (active.current !== run) return;
          setPreparing(false);
          setError(readableError(failure, { what: 'Starting this scenario' }));
        }
      };
      void launch();
    },
    [cancel, planCode],
  );

  return { intent, error, preparing, start, clear };
}
